<template>
  <AppModal :align-center="true" :closeable="false" style="width: 650px">
    <div class="d--f fd--c ai--c">
      <h3>Generating your stormwater management report</h3>
      <div class="progress-bar full-width bg-grey-12 br-10px">
        <div class="progress-fill" :style="{ width: `${progressPercentage}%` }"></div>
      </div>
      <p class="text-muted mt-10px">{{ currentStepText }}</p>
    </div>
  </AppModal>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { AppModal } from "@/components/ui";
import ReportsAPI from "@/api/reports";

const props = defineProps({
  reportName: {
    type: String,
    default: "",
  },
  reportId: {
    type: String,
    required: true,
  },
  onComplete: {
    type: Function,
    default: null,
  },
});

const processingSteps = [
  "Processing stormwater calculations...",
  "Creating runoff volume estimates...",
  "Comparing solutions...",
  "Analyzing cost benefits...",
  "Generating report content...",
  "Finalizing estimates...",
  "Preparing recommendations...",
];

const currentStep = ref(0);
const elapsedSeconds = ref(0);
const reportComplete = ref(false);
let mainTimer = null;

const currentStepText = computed(() => processingSteps[currentStep.value]);

// Asymptotic progress: starts fast, slows down, caps at 95% until complete
const progressPercentage = computed(() => {
  if (reportComplete.value) return 100;

  // Logarithmic curve that approaches but never reaches 95%
  // After 3s: ~25%, 6s: ~40%, 12s: ~60%, 20s: ~75%, 30s: ~85%, 60s: ~92%
  const progress = Math.min(95, 100 * (1 - Math.exp(-elapsedSeconds.value / 15)));
  return Math.round(progress);
});

const checkReportStatus = async () => {
  try {
    const response = await ReportsAPI.getReport(props.reportId);
    if (response.status === "completed") {
      reportComplete.value = true;
      // Give a brief moment to show 100% before completing
      setTimeout(() => {
        clearInterval(mainTimer);
        mainTimer = null;
        props.onComplete?.();
      }, 300);
    }
  } catch (error) {
    console.error("Error checking report status:", error);
  }
};

onMounted(() => {
  mainTimer = setInterval(() => {
    elapsedSeconds.value++;

    // Cycle through steps more slowly (every 4 seconds) and loop back if needed
    if (elapsedSeconds.value % 4 === 0) {
      currentStep.value = (currentStep.value + 1) % processingSteps.length;
    }

    // Check status every 2 seconds starting after 5 seconds
    if (elapsedSeconds.value >= 5 && elapsedSeconds.value % 2 === 1) {
      checkReportStatus();
    }
  }, 1000);
});
</script>

<style lang="scss" scoped>
.progress-bar {
  height: 8px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: $color-primary;
  transition: width 0.3s ease;
}
</style>
